import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Loader2, Wifi, Bell, AlertTriangle, HardDrive, Activity, ArrowRight, MapPin, UploadCloud, DatabaseBackup } from 'lucide-react';
import { apiGet } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useSystemOverview } from '@/lib/useSystemOverview';
import { fmtBytes } from '@/lib/format';
import { order } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FilePipeline } from '@/components/FilePipeline';
import { CountUp } from '@/components/CountUp';
import { LiveDot } from '@/components/LiveDot';
import type { BackupStatus } from '@/types';

/** 狀態色對應 */
function toneBadge(ok: boolean, okText: string, warnText: string) {
  return (
    <Badge variant={ok ? 'success' : 'warn'}>
      {ok ? okText : warnText}
    </Badge>
  );
}

/** 統計卡片 */
function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  warn,
}: {
  icon: typeof Wifi;
  label: string;
  value: ReactNode;
  sub: string;
  warn?: boolean;
}) {
  return (
    <Card className={warn ? 'border-warn' : undefined}>
      <CardContent className="flex items-start gap-4 p-5">
        <div className="rounded-md bg-secondary p-2.5">
          <Icon className="h-[18px] w-[18px] text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[12px] text-muted-foreground">{label}</p>
          <p className="mt-0.5 break-words text-[clamp(18px,5vw,22px)] font-bold leading-tight tabular">{value}</p>
          <p className="mt-1 break-words text-[11.5px] leading-snug text-muted-foreground">{sub}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function OverviewPage() {
  const { user } = useAuth();
  const { data, isLoading, error, dataUpdatedAt, isFetching } = useSystemOverview();
  const { data: backup } = useQuery({
    queryKey: ['backup-status'],
    queryFn: () => apiGet<BackupStatus>('/api/v1/system/backup-status'),
    enabled: user?.role === 'admin',
    refetchInterval: 30_000,
  });

  // 「更新於 N 秒前」每秒重算一次。純文字，不加 aria-live，免得每秒都被唸出來。
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  const agoSec = dataUpdatedAt ? Math.max(0, Math.round((now - dataUpdatedAt) / 1000)) : 0;

  if (isLoading) {
    return (
      <div className="grid place-items-center py-20">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="px-6 py-16 text-center text-[13px] text-destructive">
        載入失敗：{error instanceof Error ? error.message : '未知錯誤'}
      </p>
    );
  }

  if (!data) return null;

  const healthy =
    data.devices.online === data.devices.total &&
    !data.media.failed &&
    !data.jobs.failed;

  const priorities = [
    data.media.failed > 0 && {
      label: '傳輸失敗',
      detail: '檔案未完成保存，應先排除傳輸問題',
      value: data.media.failed,
      unit: '個檔案',
      href: '/media?status=failed',
      Icon: UploadCloud,
      level: '立即處理',
      action: `檢視 ${data.media.failed.toLocaleString()} 個失敗檔案`,
    },
    data.devices.online < data.devices.total && {
      label: '裝置離線',
      detail: '裝置目前無法回報事件與媒體，需確認連線',
      value: data.devices.total - data.devices.online,
      unit: '台裝置',
      href: '/devices?status=offline',
      Icon: Wifi,
      level: '立即處理',
      action: `檢查 ${(data.devices.total - data.devices.online).toLocaleString()} 台離線裝置`,
    },
    data.events.unreviewed > 0 && {
      label: '事件待確認',
      detail: '需要人工判讀並完成事件狀態',
      value: data.events.unreviewed,
      unit: '筆事件',
      href: '/events?state=unreviewed',
      Icon: AlertTriangle,
      level: '接續處理',
      action: `判讀 ${data.events.unreviewed.toLocaleString()} 筆事件`,
    },
    data.events.site_pending > 0 && {
      label: '場域待判定',
      detail: '尚未指派事件發生場域',
      value: data.events.site_pending,
      unit: '筆事件',
      href: '/events?site_resolution=pending',
      Icon: MapPin,
      level: '資料補全',
      action: `檢視 ${data.events.site_pending.toLocaleString()} 筆待判定事件`,
    },
    data.jobs.failed > 0 && {
      label: '背景工作失敗',
      detail: '驗證或歸檔工作未完成，需確認失敗原因',
      value: data.jobs.failed,
      unit: '個工作',
      href: '/media?status=failed',
      Icon: Activity,
      level: '接續處理',
      action: `檢視 ${data.jobs.failed.toLocaleString()} 個失敗工作`,
    },
    backup && backup.pending_total > 0 && {
      label: '備份逾期風險',
      detail: backup.backup_root_writable === false
        ? '備份目的地目前無法寫入，請先修正設定'
        : `最舊未備份已等待 ${Math.round((backup.oldest_unbacked_age_sec ?? 0) / 3600)} 小時`,
      value: backup.pending_total,
      unit: '個檔案',
      href: '/backup',
      Icon: DatabaseBackup,
      level: '立即處理',
      action: `處理 ${backup.pending_total.toLocaleString()} 個待備份檔案`,
    },
    data.media.in_flight > 0 && {
      label: '檔案待完成傳輸',
      detail: '包含等待上傳、上傳與驗證中的檔案',
      value: data.media.in_flight,
      unit: '個檔案',
      href: '/media?status=in_flight',
      Icon: UploadCloud,
      level: '持續追蹤',
      action: `追蹤 ${data.media.in_flight.toLocaleString()} 個傳輸中檔案`,
    },
  ].filter(Boolean) as Array<{
    label: string; detail: string; value: number; href: string;
    Icon: typeof AlertTriangle; level: string; unit: string; action: string;
  }>;

  return (
    <div className="space-y-6">
      {/* 頂部 hero：沿用原生版的深色漸層＋節點意象。
          右上角的紅色光暈與兩顆重疊的圓形節點是這個平台的視覺記憶點，
          換成白底卡片就失去了「現場監控」的氛圍。 */}
      <section
        className="reveal relative isolate grid min-h-[208px] items-center gap-8 overflow-hidden
                   rounded-[22px] p-[clamp(22px,3.2vw,46px)] sm:min-h-[236px] sm:rounded-[24px]
                   shadow-[0_24px_50px_-32px_rgba(10,10,12,0.7)]
                   lg:grid-cols-[minmax(0,1.2fr)_minmax(280px,0.8fr)]"
        style={{
          ...order(0),
          background:
            'radial-gradient(circle at 88% 12%, rgba(197,29,43,.28), transparent 30%),' +
            'linear-gradient(115deg, #28282c 0%, #151517 66%, #0e0e10 100%)',
        }}
      >
        <div className="relative z-10 max-w-[670px]">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <p className="font-mono text-[11px] tracking-[0.18em] text-[#ff7781]">
              LIVE FIELD OVERVIEW
            </p>
            {/* 全站唯一會循環的 LIVE 點：資料每 10 秒輪詢，取得中改為警示色且不脈衝 */}
            <span className="inline-flex items-center gap-2 font-mono text-[11px] text-[#c5c5ca]">
              <LiveDot busy={isFetching} />
              <span>{isFetching ? '更新中…' : `更新於 ${agoSec} 秒前`}</span>
            </span>
          </div>
          <h2 className="mt-2.5 font-display text-[clamp(27px,3vw,42px)] font-bold
                         leading-[1.12] tracking-[-0.04em] text-white">
            {healthy ? '現場訊號穩定流動中' : '有狀態需要您的注意'}
          </h2>
          <p className="mt-2.5 text-[14px] leading-relaxed text-[#c5c5ca]">
            目前有 <span className="tabular text-white"><CountUp value={data.devices.online} /></span> 台裝置上線，過去 24 小時接收{' '}
            <span className="tabular text-white"><CountUp value={data.events.last24h} /></span> 筆事件。
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2.5 sm:mt-5 sm:gap-3">
            {toneBadge(healthy, '系統運作正常', '需要處理')}
            {data.devices.online < data.devices.total && <Link to="/devices?status=offline" className="inline-flex min-h-11 items-center rounded-sm border border-white/30 px-3 text-sm font-semibold text-white hover:bg-white/10">查看離線裝置</Link>}
            {data.media.failed > 0 && <Link to="/media?status=failed" className="inline-flex min-h-11 items-center rounded-sm border border-white/30 px-3 text-sm font-semibold text-white hover:bg-white/10">查看傳輸失敗</Link>}
          </div>
        </div>

        {/* 兩顆節點刻意重疊並下移，構成有景深的組合，不是並排的兩張圖 */}
        <div className="relative z-10 hidden min-h-[150px] items-center justify-end lg:flex">
          <span className="absolute right-[108px] top-3 text-[#ff7781]">
            <Activity className="status-pulse h-5 w-5" />
          </span>
          {[
            ['edgeai-camera.png', '視覺'],
            ['edgeai-audio.png', '聲學'],
          ].map(([src, label], i) => (
            <div
              key={src}
              className="relative grid h-[136px] w-[136px] translate-y-4 place-items-center
                         rounded-full border border-[rgba(240,82,94,0.28)] bg-white/[0.045]"
              style={{ marginLeft: i === 0 ? 0 : -24 }}
            >
              <img src={`/assets/${src}`} alt={`${label}節點`}
                   className="h-[108px] w-[108px] object-contain
                              drop-shadow-[0_14px_20px_rgba(0,0,0,0.3)]" />
              <span className="absolute bottom-1.5 rounded-[10px] bg-[#0e0e10] px-2 py-0.5
                               text-[10px] text-[#ff7781]">
                {label}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* 優先處理佇列：風險與下一步集中在同一個決策區。 */}
      {priorities.length > 0 && (
        <section className="reveal overflow-hidden rounded-lg border border-border bg-card" style={order(1)} aria-labelledby="priority-heading">
          <div className="flex flex-col gap-2.5 border-b border-border px-4 py-4 sm:flex-row sm:items-end sm:justify-between sm:px-5">
            <div>
              <p className="font-mono text-[10px] tracking-[0.16em] text-primary">ACTION QUEUE</p>
              <h3 id="priority-heading" className="mt-1 font-display text-lg font-bold tracking-tight">優先處理佇列</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">依保存風險與判讀時效排列</p>
            </div>
            <span className="w-fit rounded-full bg-accent px-2.5 py-1 text-xs font-semibold text-accent-foreground">
              {priorities.length} 類待辦
            </span>
          </div>
          <div className="stagger divide-y divide-border">
            {priorities.map(({ label, detail, value, unit, href, Icon, level, action }, index) => (
              <Link
                key={`${href}-${label}`}
                to={href}
                className={`group grid grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-3 px-4 py-4 transition-colors hover:bg-accent/60 focus-visible:bg-accent/60 sm:min-h-[84px] sm:grid-cols-[40px_minmax(0,1fr)_96px_minmax(168px,auto)] sm:px-5 sm:py-3 ${index < 2 ? 'bg-destructive/[0.035]' : ''}`}
              >
                <span className={`grid h-9 w-9 place-items-center rounded-md ${index === 0 ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <span className="flex flex-wrap items-baseline gap-x-2">
                    <strong className="text-sm">{label}</strong>
                    <span className="text-[11px] font-semibold text-primary">{level}</span>
                  </span>
                  <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground sm:truncate" title={detail}>{detail}</span>
                </span>
                <span className="text-right">
                  <strong className="block font-mono text-lg leading-none tabular" title={value.toLocaleString()}><CountUp value={value} /></strong>
                  <span className="mt-1 block text-[10px] text-muted-foreground">{unit}</span>
                </span>
                <span className={`col-span-3 inline-flex min-h-11 items-center justify-center gap-2 rounded-md border px-3 text-center text-xs font-semibold transition-colors sm:col-span-1 sm:justify-between ${index === 0 ? 'border-primary bg-primary text-primary-foreground group-hover:bg-primary/90' : 'border-border bg-background text-foreground group-hover:border-primary/40'}`}>
                  <span>{action}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* 健康統計退為中性掃讀資訊。 */}
      {/* 手機也用兩欄：一欄要捲很久才看得完四張卡 */}
      <div className="reveal grid grid-cols-2 gap-3 md:gap-4 xl:grid-cols-4" style={order(2)}>
        <StatCard
          icon={Wifi}
          label="裝置上線"
          value={<><CountUp value={data.devices.online} />/{data.devices.total}</>}
          sub={data.devices.online === data.devices.total ? '全部正常' : '有裝置離線'}
          warn={data.devices.online !== data.devices.total}
        />
        <StatCard
          icon={Bell}
          label="24 小時事件"
          value={<CountUp value={data.events.last24h} />}
          sub={`累計 ${data.events.total} 筆`}
        />
        <StatCard
          icon={AlertTriangle}
          label="待確認事件"
          value={<CountUp value={data.events.unreviewed} />}
          sub={data.events.unreviewed ? '需人工檢視' : '已清空'}
          warn={data.events.unreviewed > 0}
        />
        {/* 強調規則要一致：每張卡在「自己這個指標出問題」時才亮警示。
            積壓（待傳）超過已保存，代表上傳追不上產出——這是最該被看見的狀態，
            原本這張卡永遠不警示，反而是整頁最安靜的一張。 */}
        <StatCard
          icon={HardDrive}
          label="已保存影音"
          value={<CountUp value={data.media.stored} />}
          sub={data.media.in_flight > data.media.stored
            ? `${data.media.in_flight.toLocaleString()} 筆待傳，上傳追不上`
            : fmtBytes(data.media.stored_bytes)}
          warn={data.media.in_flight > data.media.stored}
        />
      </div>

      {/* 下方兩欄：檔案流程是重點（三個階段＋失敗列），給它兩倍寬；
          背景工作只有兩個數字，一欄就夠。 */}
      <div className="reveal grid grid-cols-1 gap-4 lg:grid-cols-3" style={order(3)}>
        {/* 檔案流程狀態卡 */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div>
              <CardTitle>檔案流程狀態</CardTitle>
              <CardDescription>裝置上傳的檔案，從接收、保存到備份的即時數量</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            {/* 看得出「待完成傳輸 → 已保存 → 已備份」的先後；失敗不屬於流程的下一步，獨立成一列。
                舊版把數字塞進 44px 方塊，數字一多就超出框（2026-10-31 回報）。 */}
            <FilePipeline media={data.media} canOpenBackup={user?.role === 'admin'} />
          </CardContent>
        </Card>

        {/* 背景工作卡 */}
        <Card>
          <CardHeader>
            <div>
              <CardTitle>背景工作</CardTitle>
              <CardDescription>平台內部待處理與失敗工作</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            {/* 兩個數字並排就好，不必各佔一整塊 —— 值為 0 時整欄留白很浪費 */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md border border-border bg-secondary/50 px-4 py-3">
                <p className="text-[11.5px] text-muted-foreground">排隊中</p>
                <p className="tabular mt-1 text-[24px] font-bold leading-none">
                  <CountUp value={data.jobs.queued} />
                </p>
              </div>
              <div className={`rounded-md border px-4 py-3 ${
                data.jobs.failed > 0
                  ? 'border-destructive/30 bg-accent'
                  : 'border-border bg-secondary/50'
              }`}>
                <p className={`text-[11.5px] ${
                  data.jobs.failed > 0 ? 'text-accent-foreground' : 'text-muted-foreground'
                }`}>失敗</p>
                <p className={`tabular mt-1 text-[24px] font-bold leading-none ${
                  data.jobs.failed > 0 ? 'text-accent-foreground' : ''
                }`}>
                  <CountUp value={data.jobs.failed} />
                </p>
              </div>
            </div>
            <p className="mt-4 text-[12px] leading-relaxed text-muted-foreground">
              驗證與歸檔皆由背景工作處理；裝置需查詢最終保存結果才可刪除 SD 副本。
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
